<script setup lang="ts">
import { computed } from 'vue'
import { Notification } from '../../../stores/notification';

const props = defineProps<{
  notification: Notification
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const icon = computed(() => {
  switch (props.notification.type) {
    case 'success':
      return '✅'
    case 'error':
      return '❌'
    case 'warning':
      return '⚠️'
    case 'info':
      return 'ℹ️'
  }
})

const bgColor = computed(() => {
  switch (props.notification.type) {
    case 'success':
      return 'bg-green-50 dark:bg-green-900/50'
    case 'error':
      return 'bg-red-50 dark:bg-red-900/50'
    case 'warning':
      return 'bg-yellow-50 dark:bg-yellow-900/50'
    case 'info':
      return 'bg-blue-50 dark:bg-blue-900/50'
  }
})

const textColor = computed(() => {
  switch (props.notification.type) {
    case 'success':
      return 'text-green-800 dark:text-green-200'
    case 'error':
      return 'text-red-800 dark:text-red-200'
    case 'warning':
      return 'text-yellow-800 dark:text-yellow-200'
    case 'info':
      return 'text-blue-800 dark:text-blue-200'
  }
})

const handleClick = () => {
  props.notification.action?.onClick()
  setTimeout(() => {
    emit('close')
  }, 5000)
  
}
</script>

<template>
  <div 
    class="w-96 p-4 rounded-lg shadow-lg border animate-slide-in"
    :class="[
      bgColor,
      props.notification.type === 'success' ? 'border-green-200 dark:border-green-800' :
      props.notification.type === 'error' ? 'border-red-200 dark:border-red-800' :
      props.notification.type === 'warning' ? 'border-yellow-200 dark:border-yellow-800' :
      'border-blue-200 dark:border-blue-800'
    ]"
  >
    <div class="flex items-start gap-3">
      <!-- Icon -->
      <div class="text-xl">{{ icon }}</div>

      <!-- Content -->
      <div class="flex-1">
        <h4 class="font-medium" :class="textColor">
          {{ notification.title }}
        </h4>
        <p class="text-sm mt-1" :class="textColor">
          {{ notification.message }}
        </p>
        
        <!-- Action Button -->
        <button
          v-if="notification.action"
          @click="handleClick"
          class="mt-2 text-sm font-medium"
          :class="textColor"
        >
          {{ notification.action.label }} →
        </button>
      </div>

      <!-- Close Button -->
      <button 
        @click="$emit('close')"
        id="desktop-notification-bubble-close-button"
        class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
      >
        ✕
      </button>
    </div>
  </div>
</template>

<style scoped>
.animate-slide-in {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>